import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Play, Pause, Volume2, VolumeX, RotateCcw, ChevronRight, ChevronLeft,
  Globe, MessageSquare, Database, FileSearch, ShieldAlert, Calculator,
  GitBranch, UserCheck, RefreshCcw, Users, Loader2,
} from "lucide-react";
import Logo from "@/components/Logo";
import BrandStripe from "@/components/BrandStripe";
import { api, API } from "@/lib/api";

const AGENTS = [
  { Icon: MessageSquare, name: "Intake Concierge", role: "Attraction → Conversation" },
  { Icon: Database, name: "Data Harvesting", role: "API Orchestrator" },
  { Icon: FileSearch, name: "OCR Specialist", role: "Document Ingestion" },
  { Icon: ShieldAlert, name: "Fraud Guardian", role: "Risk Mitigation" },
  { Icon: Calculator, name: "AUS Triage", role: "Financial Analysis" },
  { Icon: GitBranch, name: "Triage & Routing", role: "Decision Matrix" },
  { Icon: UserCheck, name: "HITL Liaison", role: "LO Workspace" },
  { Icon: RefreshCcw, name: "Retention Sentinel", role: "Nurture & Re-entry" },
];

/* --------------------------- VISUAL COMPONENTS --------------------------- */

const ArchitectureViz = () => (
  <div className="h-full w-full grid grid-rows-3 gap-6">
    {[
      { t: "Attraction Layer", s: "10 → 100+ landing pages", Icon: Globe, c: "#E88C2F" },
      { t: "Processing Layer", s: "8-Agent Core + HITL", Icon: GitBranch, c: "#00A651" },
      { t: "Nurture Layer", s: "Agent 8 · Lead-leak prevention", Icon: RefreshCcw, c: "#ED1C24" },
    ].map((l, i) => (
      <motion.div
        key={l.t}
        initial={{ opacity: 0, x: -40 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.15 * i, duration: 0.6 }}
        className="flex items-center gap-5 border border-gray-200 bg-white p-5 rounded-sm border-l-4"
        style={{ borderLeftColor: l.c }}
      >
        <div className="w-14 h-14 grid place-items-center rounded-sm"
             style={{ backgroundColor: `${l.c}15`, color: l.c }}>
          <l.Icon size={28} />
        </div>
        <div>
          <p className="text-xs uppercase tracking-[0.25em] font-bold" style={{ color: l.c }}>Layer {i + 1}</p>
          <h3 className="font-heading font-bold text-2xl text-black mt-1">{l.t}</h3>
          <p className="font-sub italic text-gray-600 text-sm mt-0.5">{l.s}</p>
        </div>
      </motion.div>
    ))}
  </div>
);

const AttractionViz = () => {
  const sectors = ["FHA", "VA", "Conv", "Jumbo", "First-Time", "ITIN", "DSCR", "Bank Stmt", "Refi", "Fix-Flip"];
  return (
    <div className="h-full w-full flex flex-col gap-5">
      <div className="grid grid-cols-5 gap-3">
        {sectors.map((s, i) => (
          <motion.div
            key={s}
            initial={{ opacity: 0, scale: 0.6 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.05 * i, duration: 0.4 }}
            className="aspect-[4/3] border bg-white p-3 grid place-items-center text-center"
            style={{ borderColor: ["#00A651", "#E88C2F", "#ED1C24", "#F26522"][i % 4] }}
          >
            <div>
              <Globe size={20} className="mx-auto mb-1 text-gray-400" />
              <p className="text-xs font-heading font-bold">{s}</p>
            </div>
          </motion.div>
        ))}
      </div>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.7, duration: 0.6 }}
        className="bg-black text-white p-5 rounded-sm border-l-4 border-l-[#00A651]"
      >
        <p className="text-xs uppercase tracking-[0.25em] text-[#E88C2F] font-bold">
          Programmatic SEO
        </p>
        <p className="mt-2 font-sub italic">
          One CMS template &nbsp;→&nbsp; hundreds of pages like
          <span className="font-heading font-bold text-white ml-1.5">
            &ldquo;FHA Loans in Bartlett, TN&rdquo;
          </span>
        </p>
      </motion.div>
    </div>
  );
};

const AgentViz = ({ activeIndex }) => (
  <div className="h-full w-full grid grid-cols-4 grid-rows-2 gap-3">
    {AGENTS.map((a, i) => {
      const active = i === activeIndex;
      const palette = ["#00A651", "#E88C2F", "#ED1C24", "#F26522"];
      const c = palette[i % palette.length];
      return (
        <motion.div
          key={a.name}
          animate={{
            scale: active ? 1.04 : 1,
            boxShadow: active ? `0 14px 30px ${c}40` : "0 0 0 rgba(0,0,0,0)",
          }}
          transition={{ duration: 0.4 }}
          className={`bg-white border p-4 rounded-sm relative ${active ? "border-2" : "border border-gray-200"}`}
          style={{ borderColor: active ? c : undefined }}
        >
          <div className="flex items-center gap-2">
            <span
              className="font-heading font-bold text-xs px-2 py-0.5 rounded-sm text-white"
              style={{ backgroundColor: c }}
            >
              {String(i + 1).padStart(2, "0")}
            </span>
            <a.Icon size={16} className="text-gray-700" />
          </div>
          <p className="font-heading font-bold text-sm text-black mt-3 leading-tight">{a.name}</p>
          <p className="text-[10px] uppercase tracking-wider text-gray-500 mt-1">{a.role}</p>
        </motion.div>
      );
    })}
  </div>
);

const HitlViz = () => {
  const points = [
    { t: "Fraud Flags", s: "Document tampering → freeze for LO review", c: "#ED1C24" },
    { t: "Edge-Case Underwriting", s: "Borderline DTI (e.g., 46% on 45% cap)", c: "#E88C2F" },
    { t: "Final Sign-off", s: "One-click push to wholesale lender", c: "#00A651" },
  ];
  return (
    <div className="h-full w-full grid grid-rows-3 gap-4">
      {points.map((p, i) => (
        <motion.div
          key={p.t}
          initial={{ opacity: 0, x: 40 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2 * i, duration: 0.55 }}
          className="flex items-center gap-5 bg-white border border-gray-200 p-5 rounded-sm border-r-4"
          style={{ borderRightColor: p.c }}
        >
          <div
            className="w-14 h-14 grid place-items-center font-heading font-bold text-2xl text-white rounded-sm"
            style={{ backgroundColor: p.c }}
          >
            {String(i + 1).padStart(2, "0")}
          </div>
          <div className="flex-1">
            <h3 className="font-heading font-bold text-lg text-black">{p.t}</h3>
            <p className="font-sub italic text-gray-600 text-sm mt-1">{p.s}</p>
          </div>
          <Users className="text-black" size={22} />
        </motion.div>
      ))}
    </div>
  );
};

const OutcomeViz = () => (
  <div className="h-full w-full grid place-items-center">
    <div className="w-full max-w-xl text-center">
      <motion.div
        initial={{ scale: 0.5, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.6 }}
        className="inline-flex flex-col items-center gap-2"
      >
        <div className="w-24 h-24 grid place-items-center bg-black text-white rounded-full">
          <Users size={36} />
        </div>
        <p className="font-heading font-bold text-2xl text-black mt-3">1 Loan Originator</p>
        <p className="font-sub italic text-gray-600">orchestrating</p>
      </motion.div>
      <div className="mt-6 grid grid-cols-3 gap-3">
        {[
          { v: "100+", l: "Pages", c: "#E88C2F" },
          { v: "8", l: "AI Agents", c: "#00A651" },
          { v: "0%", l: "Lead Leakage", c: "#ED1C24" },
        ].map((s, i) => (
          <motion.div
            key={s.l}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 + 0.15 * i, duration: 0.5 }}
            className="bg-white border border-gray-200 p-4 rounded-sm border-t-4"
            style={{ borderTopColor: s.c }}
          >
            <div className="font-heading font-bold text-3xl text-black">{s.v}</div>
            <div className="text-[10px] uppercase tracking-wider text-gray-500 mt-1">{s.l}</div>
          </motion.div>
        ))}
      </div>
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1, duration: 0.6 }}
        className="font-sub italic text-lg text-black mt-8"
      >
        Insuring Your Future…Today.
      </motion.p>
    </div>
  </div>
);

const VisualRouter = ({ scene }) => {
  switch (scene.visual) {
    case "architecture": return <ArchitectureViz />;
    case "attraction":   return <AttractionViz />;
    case "agent":        return <AgentViz activeIndex={scene.agent_index} />;
    case "hitl":         return <HitlViz />;
    case "outcome":      return <OutcomeViz />;
    default:             return null;
  }
};

/* ------------------------------ MAIN PAGE ------------------------------ */

export default function Ecosystem() {
  const [scenes, setScenes] = useState([]);
  const [loadErr, setLoadErr] = useState("");
  const [idx, setIdx] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [progress, setProgress] = useState(0);
  const audioRef = useRef(null);

  // Fetch scenes from backend (includes audio URLs)
  useEffect(() => {
    api.get("/ecosystem/scenes")
      .then((r) => setScenes(r.data?.scenes || []))
      .catch(() => setLoadErr("Couldn't load the walkthrough. Please refresh."));
  }, []);

  const scene = scenes[idx];

  // Stop audio whenever the scene changes; reset progress
  useEffect(() => {
    setProgress(0);
    if (audioRef.current) {
      try { audioRef.current.pause(); } catch { /* ignore */ }
    }
  }, [idx]);

  // When playing flag turns on, start the current scene's audio
  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) {
      a.muted = muted;
      a.play().catch(() => setPlaying(false));
    } else {
      try { a.pause(); } catch { /* ignore */ }
    }
  }, [playing, idx, muted]);

  // Audio events
  const onTimeUpdate = () => {
    const a = audioRef.current;
    if (!a || !a.duration) return;
    setProgress((a.currentTime / a.duration) * 100);
  };
  const onEnded = () => {
    setProgress(100);
    if (idx < scenes.length - 1) setIdx((i) => i + 1);
    else setPlaying(false);
  };

  const togglePlay = () => setPlaying((p) => !p);
  const restart = () => { setIdx(0); setPlaying(true); };
  const goto = (i) => { if (i >= 0 && i < scenes.length) setIdx(i); };
  const toggleMute = () => {
    setMuted((m) => {
      const next = !m;
      if (audioRef.current) audioRef.current.muted = next;
      return next;
    });
  };

  const audioSrc = useMemo(() => {
    if (!scene?.audio_url) return null;
    // audio_url is /api/audio/<file>.mp3 — prefix with backend host so the browser can fetch it directly
    const base = (API || "").replace(/\/api$/, "");
    return base + scene.audio_url;
  }, [scene]);

  const sceneProgress = useMemo(
    () => (scenes.length ? ((idx + (progress / 100)) / scenes.length) * 100 : 0),
    [idx, progress, scenes.length]
  );

  if (loadErr) {
    return (
      <div className="min-h-screen grid place-items-center text-center px-6" data-testid="page-ecosystem">
        <div>
          <p className="text-[#ED1C24] font-bold uppercase tracking-wider text-sm">Error</p>
          <h1 className="font-heading font-bold text-3xl text-black mt-2">{loadErr}</h1>
        </div>
      </div>
    );
  }

  if (!scene) {
    return (
      <div className="min-h-screen grid place-items-center" data-testid="page-ecosystem">
        <div className="flex items-center gap-3 text-gray-500">
          <Loader2 className="animate-spin" size={20} />
          <span>Loading walkthrough…</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-[#F8FAFC]" data-testid="page-ecosystem">
      <BrandStripe thickness="h-1.5" />
      {/* Top bar */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" data-testid="ecosystem-back-home" className="block">
            <Logo size={36} />
          </Link>
          <div className="hidden sm:flex items-center gap-3 text-xs uppercase tracking-wider font-bold text-gray-500">
            <span className="text-[#00A651]">Ecosystem Walkthrough</span>
            <span>·</span>
            <span>Client Preview</span>
          </div>
        </div>
      </header>

      <main className="flex-1">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12 grid lg:grid-cols-12 gap-8">
          {/* LEFT — narration */}
          <section className="lg:col-span-5 flex flex-col">
            <AnimatePresence mode="wait">
              <motion.div
                key={scene.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.45 }}
                data-testid={`scene-${scene.id}`}
              >
                <p
                  className="text-xs uppercase tracking-[0.3em] font-bold mb-3"
                  style={{ color: scene.color === "#000000" ? "#ED1C24" : scene.color }}
                  data-testid="scene-chapter"
                >
                  {scene.chapter}
                </p>
                <h1
                  className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl text-black leading-[1.1] tracking-tight"
                  data-testid="scene-title"
                >
                  {scene.title}
                </h1>
                <div
                  className="h-1 w-20 mt-5 mb-6"
                  style={{ backgroundColor: scene.color === "#000000" ? "#ED1C24" : scene.color }}
                />
                <p
                  className="font-sub italic text-lg text-gray-700 leading-relaxed"
                  data-testid="scene-narration"
                >
                  {scene.narration}
                </p>
              </motion.div>
            </AnimatePresence>

            {/* Chapter selector */}
            <div className="mt-10">
              <p className="text-xs uppercase tracking-[0.25em] font-bold text-gray-500 mb-4">
                Chapters
              </p>
              <div className="space-y-1 max-h-64 overflow-y-auto pr-2">
                {scenes.map((s, i) => (
                  <button
                    key={s.id}
                    onClick={() => goto(i)}
                    data-testid={`chapter-btn-${i}`}
                    className={`group w-full flex items-center gap-3 px-3 py-2 text-left rounded-sm transition-colors ${
                      i === idx ? "bg-black text-white" : "hover:bg-white text-gray-700"
                    }`}
                  >
                    <span className={`font-heading font-bold text-xs w-6 ${i === idx ? "text-[#E88C2F]" : ""}`}>
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="text-sm font-bold flex-1 truncate">{s.title}</span>
                    <ChevronRight
                      size={14}
                      className={i === idx ? "opacity-100" : "opacity-0 group-hover:opacity-50"}
                    />
                  </button>
                ))}
              </div>
            </div>
          </section>

          {/* RIGHT — visualization */}
          <section className="lg:col-span-7">
            <div
              className="bg-white border border-gray-200 rounded-sm aspect-[4/3] lg:aspect-auto lg:h-[560px] p-6 sm:p-8 overflow-hidden relative"
              data-testid="scene-visual"
            >
              <AnimatePresence mode="wait">
                <motion.div
                  key={scene.id}
                  initial={{ opacity: 0, scale: 0.96 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 1.02 }}
                  transition={{ duration: 0.5 }}
                  className="h-full w-full"
                >
                  <VisualRouter scene={scene} />
                </motion.div>
              </AnimatePresence>
            </div>
          </section>
        </div>

        {/* Player */}
        <div className="sticky bottom-0 bg-white border-t border-gray-200">
          <div className="h-1 bg-gray-100">
            <motion.div
              className="h-full"
              style={{ backgroundColor: scene.color === "#000000" ? "#ED1C24" : scene.color }}
              animate={{ width: `${sceneProgress}%` }}
              transition={{ duration: 0.2 }}
            />
          </div>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <button
                onClick={() => goto(idx - 1)}
                disabled={idx === 0}
                data-testid="player-prev"
                className="w-10 h-10 grid place-items-center border border-gray-200 hover:border-black disabled:opacity-40 rounded-sm"
                aria-label="Previous"
              >
                <ChevronLeft size={18} />
              </button>
              <button
                onClick={togglePlay}
                data-testid="player-play"
                className="px-5 h-10 inline-flex items-center gap-2 bg-[#00A651] hover:bg-[#008f45] text-white font-bold uppercase tracking-wider text-xs rounded-sm transition-colors"
                aria-label={playing ? "Pause narration" : "Play narration"}
              >
                {playing ? <Pause size={16} /> : <Play size={16} />}
                {playing ? "Pause" : "Play Walkthrough"}
              </button>
              <button
                onClick={() => goto(idx + 1)}
                disabled={idx === scenes.length - 1}
                data-testid="player-next"
                className="w-10 h-10 grid place-items-center border border-gray-200 hover:border-black disabled:opacity-40 rounded-sm"
                aria-label="Next"
              >
                <ChevronRight size={18} />
              </button>
              <button
                onClick={restart}
                data-testid="player-restart"
                className="w-10 h-10 grid place-items-center border border-gray-200 hover:border-black rounded-sm"
                aria-label="Restart"
              >
                <RotateCcw size={16} />
              </button>
              <button
                onClick={toggleMute}
                data-testid="player-mute"
                className={`w-10 h-10 grid place-items-center border rounded-sm ${
                  muted ? "border-[#ED1C24] text-[#ED1C24]" : "border-gray-200 hover:border-black"
                }`}
                aria-label={muted ? "Unmute" : "Mute"}
              >
                {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
              </button>
            </div>
            <div className="text-xs uppercase tracking-wider text-gray-500 font-bold hidden sm:block">
              Scene <span className="text-black">{idx + 1}</span> / {scenes.length}
              {!audioSrc && (
                <span className="ml-3 text-[#ED1C24]">Audio unavailable</span>
              )}
            </div>
          </div>
        </div>
      </main>
      <BrandStripe thickness="h-1.5" />

      {/* Single hidden audio element; src swaps per scene */}
      <audio
        ref={audioRef}
        src={audioSrc || undefined}
        onTimeUpdate={onTimeUpdate}
        onEnded={onEnded}
        preload="auto"
        data-testid="ecosystem-audio"
      />
    </div>
  );
}
